import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  ImageList,
  ImageListItem,
  IconButton,
  Badge,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    area: '',
    property_type: '',
    status: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'Kenya',
    amenities: [],
    images: []
  });

  // Property types and statuses
  const propertyTypes = [
    'residential',
    'commercial',
    'agricultural',
    'industrial',
    'land'
  ];

  const statusOptions = [
    'for-sale',
    'for-rent',
    'sold',
    'rented'
  ];

  const amenitiesList = [
    'Swimming Pool',
    'Gym',
    'Parking',
    'Garden',
    'Security',
    'WiFi',
    'Air Conditioning',
    'Heating',
    'Balcony',
    'Elevator'
  ];

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      
      if (!user?.email) {
        setError('User not authenticated');
        return;
      }

      console.log('=== EDIT PROPERTY DEBUG START ===');
      console.log('1. Property ID:', id);
      console.log('2. User email:', user.email);

      // Get property
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single();

      console.log('3. Property data:', property);
      console.log('4. Property error:', propertyError);

      if (propertyError) {
        setError('Property not found');
        return;
      }

      // Verify ownership
      const { data: customUser, error: userError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', user.email)
        .single();

      console.log('5. Custom user:', customUser);
      console.log('6. User error:', userError);

      if (userError) {
        setError('User verification failed');
        return;
      }

      // Check if user owns this property
      const isOwnerByInteger = property.owner_id === customUser.id;
      const isOwnerByUUID = property.owner_id === user.id;
      
      console.log('7. Ownership check:');
      console.log('   - Property owner_id:', property.owner_id);
      console.log('   - Property owner_id type:', typeof property.owner_id);
      console.log('   - Custom user ID (integer):', customUser.id);
      console.log('   - Supabase user ID (UUID):', user.id);
      console.log('   - Is owner by integer?', isOwnerByInteger);
      console.log('   - Is owner by UUID?', isOwnerByUUID);
      
      if (!isOwnerByInteger && !isOwnerByUUID) {
        setError('You do not have permission to edit this property');
        return;
      }

      console.log('8. User is verified owner, populating form...');

      // Populate form with property data
      setFormData({
        title: property.title || '',
        description: property.description || '',
        price: property.price || '',
        area: property.area || '',
        property_type: property.property_type || '',
        status: property.status || '',
        address: property.address || '',
        city: property.city || '',
        state: property.state || '',
        zip_code: property.zip_code || '',
        country: property.country || 'Kenya',
        amenities: property.amenities || [],
        images: property.images || []
      });
      
      console.log('9. Form populated successfully');
      console.log('=== EDIT PROPERTY DEBUG END ===');
      
      setError('');
    } catch (err) {
      console.error('Error fetching property:', err);
      setError('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Handle drag and drop for images
  const handleOnDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(formData.images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormData(prev => ({
      ...prev,
      images: items
    }));
  };

  const removeImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      // Validate required fields
      if (!formData.title || !formData.price || !formData.area) {
        setError('Please fill in all required fields');
        return;
      }

      // Update property
      const { error: updateError } = await supabase
        .from('properties')
        .update({
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          area: parseFloat(formData.area),
          property_type: formData.property_type,
          status: formData.status,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country,
          amenities: formData.amenities,
          images: formData.images, // Save the reordered images
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      setSuccessMessage('Property updated successfully!');
      
      // Redirect back to edit properties page after a short delay
      setTimeout(() => {
        navigate('/edit-properties');
      }, 2000);
      
    } catch (err) {
      console.error('Error updating property:', err);
      setError('Failed to update property. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/edit-properties')}
          sx={{ mb: 2 }}
        >
          Back to Properties
        </Button>
        
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Edit Property
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Update your property details and settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Property Title *"
                      value={formData.title}
                      onChange={handleChange('title')}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={4}
                      value={formData.description}
                      onChange={handleChange('description')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price (KES) *"
                      type="number"
                      value={formData.price}
                      onChange={handleChange('price')}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Area (acres) *"
                      type="number"
                      value={formData.area}
                      onChange={handleChange('area')}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        value={formData.property_type}
                        onChange={handleChange('property_type')}
                        label="Property Type"
                      >
                        {propertyTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        onChange={handleChange('status')}
                        label="Status"
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status.replace('-', ' ').toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Location */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Location</Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={handleChange('address')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={handleChange('city')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State/County"
                      value={formData.state}
                      onChange={handleChange('state')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.zip_code}
                      onChange={handleChange('zip_code')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country}
                      onChange={handleChange('country')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Amenities</Typography>
                
                <Grid container spacing={1}>
                  {amenitiesList.map((amenity) => (
                    <Grid item key={amenity}>
                      <Button
                        variant={formData.amenities.includes(amenity) ? "contained" : "outlined"}
                        size="small"
                        onClick={() => handleAmenityToggle(amenity)}
                      >
                        {amenity}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Images */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Property Images
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                    (Drag to reorder • First image = Cover photo)
                  </Typography>
                </Typography>
                
                {formData.images && formData.images.length > 0 ? (
                  <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="images" direction="horizontal">
                      {(provided) => (
                        <Box
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            p: 2,
                            border: '2px dashed #e0e0e0',
                            borderRadius: 2,
                            minHeight: 200
                          }}
                        >
                          {formData.images.map((image, index) => (
                            <Draggable key={`image-${index}`} draggableId={`image-${index}`} index={index}>
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    width: 180,
                                    height: 160,
                                    position: 'relative',
                                    transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                                    boxShadow: snapshot.isDragging ? 4 : 1,
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' }
                                  }}
                                >
                                  {/* Cover Photo Badge */}
                                  {index === 0 && (
                                    <Badge
                                      badgeContent={
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                                          <StarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                          <Typography variant="caption" sx={{ fontSize: 10 }}>
                                            COVER
                                          </Typography>
                                        </Box>
                                      }
                                      color="primary"
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        zIndex: 2,
                                        '& .MuiBadge-badge': {
                                          minWidth: 'auto',
                                          height: 20,
                                          padding: '0 6px',
                                          borderRadius: 10
                                        }
                                      }}
                                    />
                                  )}
                                  
                                  {/* Image */}
                                  <Box
                                    component="img"
                                    src={image}
                                    alt={`Property ${index + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: 120,
                                      objectFit: 'cover',
                                      borderRadius: '4px 4px 0 0'
                                    }}
                                  />
                                  
                                  {/* Controls */}
                                  <Box
                                    sx={{
                                      height: 40,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      px: 1,
                                      backgroundColor: 'background.paper'
                                    }}
                                  >
                                    {/* Drag Handle */}
                                    <Box
                                      {...provided.dragHandleProps}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: 'text.secondary',
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' }
                                      }}
                                    >
                                      <DragIcon fontSize="small" />
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                                        #{index + 1}
                                      </Typography>
                                    </Box>
                                    
                                    {/* Delete Button */}
                                    <Tooltip title="Remove image">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeImage(index)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      border: '2px dashed #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No images uploaded
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    💡 <strong>How to use:</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    • Drag images to reorder them<br/>
                    • The first image becomes the cover photo on property cards<br/>
                    • Click the trash icon to remove an image<br/>
                    • Image upload feature coming soon
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/edit-properties')}
                disabled={saving}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default EditProperty;
